import React, { useMemo } from 'react';
import { Stack, StackDirection, useBreakpointValue } from '@chakra-ui/react';

import DashboardCard from 'Components/Molecules/DashboardCard';
import { useReports } from 'Hooks/reports';
import getCurrencyString from 'Helpers/getCurrencyString';

interface ICard {
  margin: string;
  total: string;
  volume: string;
  profit: string;
  period: string;
}

interface IProps {
  marketplaceId: string;
  vendorId?: string;
}

const DashboardCompleteTradeCards: React.FC<IProps> = ({
  marketplaceId,
  vendorId,
}) => {
  const { getReport } = useReports();

  const direction = useBreakpointValue<StackDirection>({
    base: 'column',
    md: 'row',
  });

  const stats = useMemo(
    () =>
      getReport({
        name: 'stats',
        marketplace_id: marketplaceId,
        vendor_id: vendorId,
      }),
    [getReport, marketplaceId, vendorId],
  );

  const cards = useMemo<ICard[]>(() => {
    if (!stats) return [];

    const periods = ['yearly', 'monthly', 'weekly', 'daily'];

    return periods.map<ICard>(period => ({
      margin: `Margin: ${stats[period].average_margin.toFixed(2)}%`,
      total: `Total: ${stats[period].total}`,
      volume: getCurrencyString(stats[period].amount),
      profit: getCurrencyString(stats[period].profit),
      period,
    }));
  }, [stats]);

  return (
    <Stack
      direction={direction}
      spacing="25px"
      mb={cards.length > 0 ? '25px' : '0px'}
    >
      {cards.map((card, i) => (
        <DashboardCard
          key={i}
          items={[
            {
              title: {
                text: 'Volume:',
                color: 'primary',
                size: 'large',
              },
              subtitle: {
                text: card.volume,
                color: 'primary',
                size: 'large',
              },
            },
            {
              title: {
                text: 'Profit:',
                color: 'secondary',
                size: 'large',
              },
              subtitle: {
                text: card.profit,
                color: 'secondary',
                size: 'large',
              },
            },
          ]}
          subItems={[
            {
              title: { text: card.margin },
            },
            {
              title: { text: card.total },
            },
          ]}
          period={card.period}
        />
      ))}
    </Stack>
  );
};

export default DashboardCompleteTradeCards;
